import {
    Box,
    Grid,
    Typography,
    Container,
    useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useEffect } from "react";
import CountUp from 'react-countup';
import PropTypes from 'prop-types';

const CurrentStatus = ({ language = 'EN' }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const controls = useAnimation();
    const [ref, inView] = useInView({
        threshold: 0.2,
        triggerOnce: true
    });

    // Language content
    const content = {
        EN: {
            title: "Why Choose Us?",
            subtitle: "Our achievements speak for themselves. Here's what sets us apart in the beauty education industry.",
            stats: [
                { value: 225, label: "Certified entities by local government", valueColor: "#FFD700" },
                { value: 20, label: "Copyrighted materials", valueColor: "#87CEEB" },
                { value: 5000, label: "Beauty salon owners in our network", valueColor: "#98FB98" },
                { value: 20000, label: "Beauty stakeholders data", valueColor: "#FFA07A" }
            ]
        },
        BM: {
            title: "Kenapa Pilih Kami?",
            subtitle: "Pencapaian kami bercakap untuk diri sendiri. Inilah yang membezakan kami dalam industri pendidikan kecantikan.",
            stats: [
                { value: 225, label: "Entiti bertauliah oleh kerajaan tempatan", valueColor: "#FFD700" },
                { value: 20, label: "Bahan berhak cipta", valueColor: "#87CEEB" },
                { value: 5000, label: "Pemilik salon kecantikan dalam rangkaian kami", valueColor: "#98FB98" },
                { value: 20000, label: "Data pemegang kepentingan kecantikan", valueColor: "#FFA07A" }
            ]
        },
        CN: {
            title: "为什么选择我们？",
            subtitle: "我们的成就说明一切。以下是我们在美业教育行业中脱颖而出的原因。",
            stats: [
                { value: 225, label: "经当地政府认证的实体", valueColor: "#FFD700" },
                { value: 20, label: "版权材料", valueColor: "#87CEEB" },
                { value: 5000, label: "我们网络中的美业院老板", valueColor: "#98FB98" },
                { value: 20000, label: "美业行业相关数据", valueColor: "#FFA07A" }
            ]
        }
    };

    const currentContent = content[language] || content.EN;

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        }
    }, [controls, inView]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.3,
                delayChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { y: 30, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                type: "spring",
                stiffness: 100,
                damping: 10
            }
        }
    };

    return (
        <Box
            id="stats"
            ref={ref}
            sx={{
                position: 'relative',
                minHeight: isMobile ? '60vh' : '80vh',
                display: 'flex',
                alignItems: 'center',
                py: 8,
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, #2a2a2a 100%)`,
                color: 'white'
            }}
        >
            <Container>
                <motion.div
                    initial="hidden"
                    animate={controls}
                    variants={containerVariants}
                >
                    <Typography
                        variant={isMobile ? "h3" : "h2"}
                        align="center"
                        gutterBottom
                        sx={{
                            mb: 6,
                            fontWeight: 700,
                            letterSpacing: 1.2,
                            color: '#FFFFFF'
                        }}
                    >
                        {currentContent.title}
                    </Typography>

                    <Typography
                        variant={isMobile ? "body1" : "h6"}
                        align="center"
                        sx={{
                            mb: 8,
                            maxWidth: 800,
                            mx: 'auto',
                            color: 'rgba(255,255,255,0.8)'
                        }}
                    >
                        {currentContent.subtitle}
                    </Typography>

                    <Grid container spacing={2}>
                        {currentContent.stats.map((stat, index) => (
                            <Grid item xs={6} sm={3} key={index}>
                                <motion.div variants={itemVariants}>
                                    <Box
                                        sx={{
                                            p: 3,
                                            height: '100%',
                                            textAlign: 'center',
                                            '&:hover': {
                                                transform: 'translateY(-5px)',
                                                transition: 'transform 0.3s ease'
                                            }
                                        }}
                                    >
                                        <Typography
                                            variant={isMobile ? "h4" : "h2"}
                                            gutterBottom
                                            sx={{
                                                fontWeight: 800,
                                                color: stat.valueColor,
                                                textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                                mb: 2
                                            }}
                                        >
                                            {inView && (
                                                <CountUp
                                                    end={stat.value}
                                                    duration={2.5}
                                                    suffix={stat.value === 20 ? "+" : stat.value >= 1000 ? "+" : ""}
                                                />
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: 'rgba(255,255,255,0.9)',
                                                fontWeight: 500
                                            }}
                                        >
                                            {stat.label}
                                        </Typography>
                                    </Box>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>
            </Container>
        </Box>
    );
};

CurrentStatus.propTypes = {
    language: PropTypes.oneOf(['EN', 'BM', 'CN'])
};

export default CurrentStatus;